"use client";

import { KeyRound } from "lucide-react";

import { SiteHeader } from "@/components/shell/site-header";

import { authRepository } from "@/features/auth/api/auth-repository";
import type { RequestScenario } from "@/features/auth/components/auth-request-content";
import { useEmailRequestPage } from "@/features/auth/components/auth-request-model";
import {
  BackToLogin,
  CTA_BASE,
  FIELD_BASE,
  RequestNotice,
  Spinner,
} from "@/features/auth/components/auth-request-shared";
import { FORGOT_COPY } from "@/features/auth/components/forgot/forgot-content";

/**
 * `/forgot` — the "email me a new password" screen.
 *
 * Promoted from the four-way design bake-off ("Split panel", Railway) once it
 * was approved; B, C and D and the `?variant=` harness are gone. They stay in
 * `6d31491` if the comparison is ever wanted again.
 *
 * The same full-bleed two-column split `/login`, `/signup`, `/signup/activate`
 * and `/signup/resend_activation` all use: a plum brand half carrying an
 * oversized glyph and the heading, a working half carrying the message and the
 * form. Both halves reach the viewport edges, so there is no dead space at any
 * width. Consistency is why this candidate won — every account-recovery and
 * sign-up screen now shares one composition. A key glyph rather than the resend
 * page's envelope, so the two siblings stay distinguishable at a glance.
 *
 * Note what this page promises: the backend mails a GENERATED PASSWORD, not a
 * reset link. See `forgot-content` — the wording is the live site's, and the
 * `AuthCard` this replaced had it wrong.
 *
 * No footer, matching `/signup/activate` and the site-wide rule that v5 has none
 * anywhere. The page carries the real site nav rather than `<AuthShell>`.
 */
export function ForgotView({ scenario }: { scenario: RequestScenario }) {
  const { email, setEmail, state, notice, busy, done, onSubmit } =
    useEmailRequestPage({
      scenario,
      copy: FORGOT_COPY,
      submit: (value) => authRepository.forgotPassword(value),
    });

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background font-sans text-foreground">
      <SiteHeader />

      <div className="grid flex-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <aside
          className="relative flex flex-col overflow-hidden px-8 py-14 lg:px-14"
          style={{
            background:
              "hsl(var(--card))",
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-px lg:block"
            style={{
              background:
                "hsl(var(--hairline))",
            }}
          />

          <div className="relative my-auto">
            <span className="mb-8 grid size-14 place-items-center rounded-2xl border border-primary/25 bg-primary/10 lg:size-16">
              <KeyRound aria-hidden="true" className="size-7 text-primary-text" />
            </span>
            <h1 className="max-w-[12ch] text-[2rem] font-bold tracking-tight lg:text-[3rem] lg:leading-[1.05]">
              {FORGOT_COPY.title}
            </h1>
            <div className="mt-7 h-px w-16 bg-primary" />
          </div>
        </aside>

        <main
          id="main-content"
          className="relative grid place-items-center overflow-hidden px-6 py-14 lg:px-16"
        >

          <div className="relative w-full max-w-[30rem]">
            <p className="text-[17px] leading-relaxed text-muted-foreground">
              {FORGOT_COPY.message}
            </p>

            {notice ? (
              <RequestNotice tone={state === "sent" ? "success" : "error"} className="mt-8">
                {notice}
              </RequestNotice>
            ) : null}

            {done ? (
              <div className="mt-8">
                <BackToLogin />
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="sr-only">
                    {FORGOT_COPY.placeholder}
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    required
                    autoComplete="email"
                    disabled={busy}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={FORGOT_COPY.placeholder}
                    className={FIELD_BASE}
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className={`${CTA_BASE} w-full`}
                >
                  {busy ? <Spinner /> : null}
                  {FORGOT_COPY.submit}
                </button>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
